import { React, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './lineups.module.css'
import constants from '../../../constants.js'
import api from '../../../routes/api.js'
import utils from '../../../utils/utils.js';

import { useSidebarProvider } from '../../../providers/SidebarProvider.js';
import { useApiProvider } from '../../../providers/ApiProvider.js';
import { useLeagueTypeProvider } from '../../../providers/LeagueTypeProvider';

import Select from "../../common/Select/Select.js"

const Lineups = ({ setSelectedMenuItem, selectedLocation, selectedDivision }) => {
  const { setIsSidebarShowing } = useSidebarProvider();
  const { setDialog, isLoading, setIsLoading, LoadingSpinner } = useApiProvider();
  const { leagueType } = useLeagueTypeProvider();

  const navigate = useNavigate();

  const [selectedWeek, setSelectedWeek] = useState();
  const [allWeeks, setAllWeeks] = useState([]);
  const [allMatchups, setAllMatchups] = useState([]);
  const [playoffs, setPlayoffs] = useState([]);
  const [selectedWeekMatchups, setSelectedWeekMatchups] = useState([]);
  const [teams, setTeams] = useState([]);

  const intervalRef = useRef(null);
  const intervalStartedRef = useRef(false);
  const selectedDivisionRef = useRef(selectedDivision);
  const selectedWeekRef = useRef(selectedWeek);


  const getPreviousMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)

    // How many days to subtract to get to the most recent Monday
    // If today IS Monday (1), we want this Monday (0 days back)
    // If today is Sunday (0), we want 6 days back
    const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const previousMonday = new Date(today);
    previousMonday.setDate(today.getDate() - daysBack);
    previousMonday.setHours(0, 0, 0, 0); // Start of day

    return previousMonday;
  };

  const getUpcomingMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    // Days until next Monday
    // If today IS Monday (1), we want 7 days ahead (next Monday, not today)
    const daysAhead = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7;

    const upcomingMonday = new Date(today);
    upcomingMonday.setDate(today.getDate() + daysAhead);
    upcomingMonday.setHours(0, 0, 0, 0);

    return upcomingMonday;
  };

  const getRegularWeek = (regularMatches) => {
    // Filter out all of the games that happened before this previous Monday.
    // This will leave us with only the games for the current week.
    const filteredMatches = regularMatches.filter((game) => {
      const scheduledTime = new Date(game.scheduledTime);
      return scheduledTime > getPreviousMonday();
    });

    //TODO: Sort here for peace of mind? games should be ordered by week by default
    // If there are no filtered matchups, then just return the last week. Most likely playoffs haven't started
    return filteredMatches.length > 0 ? filteredMatches[0][constants.weekNumber] : regularMatches[regularMatches.length - 1][constants.weekNumber];
  }

  const getPlayoffWeek = (weekList) => { }

  const filterMatchupsByWeek = (matchups, week) => {
    if (!matchups) return;

    let filteredMatchups = []

    if (typeof week == "string") {
      const playoffWeek = week[week.length - 1];
      const playoffs = matchups.filter((m) => {
        return m[constants.playoffs] == true;
      })
      filteredMatchups = playoffs.filter((matchup) => {
        return matchup[constants.weekNumber] == playoffWeek;
      });

    }
    else {
      filteredMatchups = matchups.filter((matchup) => {
        return matchup[constants.weekNumber] == week && matchup[constants.playoffs] == false;
      });
    }

    return filteredMatchups;
  }

  const filterWeeksForDropdown = (matchups, playoffMatches) => {
    let filteredWeeks = new Set();
    let playoffWeeks = new Set();
    let options = [];

    // Get each unique week
    matchups.forEach((matchup) => {
      if (matchup.hasOwnProperty(constants.weekNumber)) {
        filteredWeeks.add(matchup[constants.weekNumber]);
      }
    });

    playoffMatches.forEach((matchup) => {
      if (matchup.hasOwnProperty(constants.weekNumber)) {
        playoffWeeks.add(matchup[constants.weekNumber]);
      }
    });

    // Sort weeks
    const sortedWeeks = Array.from(filteredWeeks).sort((a, b) => {
      if (a < b) return 1
      if (a > b) return -1;
      return 0;
    });

    // Build playoff weeks, apologies for the slop
    const uniquePlayoffs = Array.from(playoffWeeks);
    const poRound1Count = playoffMatches.filter(m => m[constants.playoffs] && m[constants.weekNumber] == 1).length;
    let start = poRound1Count == 8 ? 0 : poRound1Count == 4 ? 1 : 2;

    uniquePlayoffs.forEach(m => {
      let option = {};
      option.key = "playoff" + m;
      option.value = "playoff" + m;
      option.label = constants.playoffRoundsShort[start];

      options.push(option);
      start++;
    })
    options.reverse();

    // Build array for dropdown select
    sortedWeeks.forEach((week) => {
      let option = {};
      option.key = "week" + week;
      option.value = week;
      option.label = "Week " + week;

      options.push(option);
    });

    return options;
  }

  const isDateInPast = (matchTime) => {
    const matchDate = new Date(matchTime);
    const currentDate = new Date();

    return matchDate < currentDate;
  }

  // Weeks can be skipped due to bye's. Can't just -1 +1 need to calc it
  const onWeekChange = (e) => {
    let newWeek;
    const index = allWeeks.findIndex(w => w.value == selectedWeek);

    // The back button was clicked
    if (e.type == "click" && e.currentTarget.id == "left") {
      if (index < allWeeks.length - 1) {
        // allWeeks is reverse sorted
        newWeek = allWeeks[index + 1].value;
        setSelectedWeek(newWeek);
        setSelectedWeekMatchups(filterMatchupsByWeek(allMatchups, newWeek));
      }
    }
    // The forward button was clicked
    else if (e.type == "click" && e.currentTarget.id == "right") {
      if (index > 0) {
        // allWeeks is reverse sorted
        newWeek = allWeeks[index - 1].value;
        setSelectedWeek(newWeek);
        setSelectedWeekMatchups(filterMatchupsByWeek(allMatchups, newWeek));
      }
    }
    // The drop down was changed 
    else if (e.type == "change") {
      newWeek = e.currentTarget.value.startsWith('playoff') ? e.currentTarget.value : parseInt(e.currentTarget.value)
      setSelectedWeek(newWeek);
      setSelectedWeekMatchups(filterMatchupsByWeek(allMatchups, newWeek));
    }
  }

  const gotoLineup = (e) => {
    const matchup = allMatchups.find((m) => m[constants.matchupId] == e.currentTarget.id);
    navigate('/' + leagueType + '/matchup', {
      replace: true,
      state: {
        selectedLocation: selectedLocation,
        selectedDivision: selectedDivision,
        matchup: {
          matchupId: e.currentTarget.id,
          matchupDate: e.currentTarget.children[1].innerHTML,
          homeClub: matchup[constants.homeClub],
          homeTeamName: matchup[constants.homeName].toUpperCase(),
          homeTeamId: matchup[constants.homeTeamId],
          awayClub: matchup[constants.awayClub],
          awayTeamName: matchup[constants.awayName].toUpperCase(),
          awayTeamId: matchup[constants.awayTeamId],
        }
      }
    });
  }

  const updateMatchupScores = async () => {
    try {
      const baseUrl = utils.getBaseUrl(leagueType);

      const matchupsResponse = await api.fetch(baseUrl, constants.uri.matchups(selectedDivisionRef.current[constants.divisionId]));
      const playoffResponse = await api.fetch(baseUrl, constants.uri.matchups(selectedDivision[constants.divisionId]), "?playoffs=true");

      if (playoffResponse.hasOwnProperty('errors')) throw playoffResponse;
      if (!matchupsResponse.hasOwnProperty([constants.values])) throw matchupsResponse;

      setAllMatchups([...matchupsResponse[constants.values], ...playoffResponse[constants.values]]);
      setSelectedWeekMatchups(filterMatchupsByWeek([...matchupsResponse[constants.values], ...playoffResponse[constants.values]], selectedWeekRef.current));
    }
    catch (err) {
      console.error(err);
      setDialog({
        title: err.title ? err.title : constants.messages.errorMessage,
        code: err.status ? err.status : "N/A",
        message: err.message,
        isError: true
      });
    }
  }

  // This useEffect is to set the state values in ref's for the interval, see other useEffect
  useEffect(() => {
    selectedWeekRef.current = selectedWeek;
    selectedDivisionRef.current = selectedDivision;
  }, [selectedWeek, selectedDivision]);

  // This useEffect is the general "initial page load" method, see other useEffect
  useEffect(() => {
    if (isLoading) return; // Wait for data from App.js

    const getLineups = async () => {
      setIsLoading(true);
      try {
        const baseUrl = utils.getBaseUrl(leagueType);

        const matchupsResponse = await api.fetch(baseUrl, constants.uri.matchups(selectedDivision[constants.divisionId]));
        const teamsResponse = await api.fetch(baseUrl, constants.uri.standings(selectedDivision[constants.divisionId]));
        const playoffResponse = await api.fetch(baseUrl, constants.uri.matchups(selectedDivision[constants.divisionId]), "?playoffs=true");

        if (playoffResponse.hasOwnProperty('errors')) throw playoffResponse;
        if (!matchupsResponse.hasOwnProperty([constants.values])) throw matchupsResponse;
        if (!teamsResponse.hasOwnProperty([constants.values])) throw teamsResponse;

        const regularMatchups = matchupsResponse[constants.values] ?? [];
        const playoffMatchups = playoffResponse[constants.values] ?? [];
        const combinedMatchups = [...regularMatchups, ...playoffMatchups];

        setTeams(teamsResponse[constants.values]);

        if (combinedMatchups.length === 0) {
          setSelectedWeek(1);
          setAllWeeks([]);
          setAllMatchups([]);
          setPlayoffs([]);
          setSelectedWeekMatchups([]);
        } else {
          const weekList = filterWeeksForDropdown(regularMatchups, playoffMatchups);

          // The first element in weekList is the latest playoff match, if it's the playoffs vs the regular matches which are created for the whole season.
          // Because of this and bye weeks, we have to calculate the week based on the date (every Monday is a new week)
          const selectedWeek =
            playoffMatchups.length > 0
              ? weekList[0].value
              : getRegularWeek(regularMatchups);

          setSelectedWeek(selectedWeek);
          setAllWeeks(weekList);
          setAllMatchups(combinedMatchups);
          setPlayoffs(playoffMatchups);
          setSelectedWeekMatchups(filterMatchupsByWeek(combinedMatchups, selectedWeek));
        }
        setIsLoading(false);
      }
      catch (err) {
        console.error(err);
        setDialog({
          title: err.title ? err.title : constants.messages.errorMessage,
          code: err.status ? err.status : "N/A",
          message: err.message,
          isError: true
        });
        setIsLoading(false);
      }
    }

    // Update the score every 5 minutes
    if (!intervalStartedRef.current) {
      intervalRef.current = setInterval(() => {
        updateMatchupScores();
      }, 300000);
      intervalStartedRef.current = true;
    }

    // If the page is loading on a small width, hide the sidebar so you see the page
    if (window.innerWidth < 1300) {
      setIsSidebarShowing(false);
    }
    setSelectedMenuItem(constants.sidebarItems[1]);
    if (selectedDivision[constants.divisionId] != "") getLineups();
  }, [selectedDivision])

  const noWeeks = !allWeeks || allWeeks.length === 0;

  const weekOptions = noWeeks
    ? [{ key: "week1", value: 1, label: "Week 1" }]
    : allWeeks;

  const displayedWeek = noWeeks ? 1 : selectedWeek;
  return (
    <div className={styles.Lineups}>

      <div className={styles.Header}>
        <div className={styles.Title}>{constants.titles.lineups}</div>
        <div className={styles.SubTitle}> {`${selectedLocation[constants.locationName]} ${selectedDivision[constants.divisionName]} DIVISION`}</div>
      </div>

      <hr className={styles.Spacer} />

      {!isLoading ? <>
        <div className={styles.WeekSelectContainer}>
          <button id="left" className={styles.WeekButton} onClick={onWeekChange}><img src='/icons/arrow-left.svg' /></button>
          <Select
            disabled={noWeeks}
            onChange={onWeekChange}
            currentValue={displayedWeek}
            options={weekOptions}
            additionalStyle={styles.WeekSelect}
          />
          <button id="right" className={styles.WeekButton} onClick={onWeekChange}><img src='/icons/arrow-right.svg' /></button>
        </div>

        <div className={styles.MatchupsContainer}> <div className={styles.MatchupsTitle}>
          {noWeeks
            ? "Week 1 Matchups"
            : typeof selectedWeek == "string"
              ? constants.playoffRounds[
              constants.playoffRoundsShort.indexOf(
                allWeeks[allWeeks.findIndex(w => w.value == selectedWeek)].label
              )
              ]
              : "Week " + selectedWeek + " Matchups"}
        </div>
          {noWeeks ? (
            <div className={styles.MatchupRow}>
              <div className={styles.MatchupTeams}>

                <div
                  className={styles.TeamLogo}
                  style={{ borderLeft: `2px solid ${constants.colors.cplYellow}` }}
                >
                </div>

                <div className={styles.Team} style={{ justifyContent: 'flex-start' }}>
                  <div
                    className={styles.TeamText}
                    style={{ height: '2em', marginRight: '.5em', textAlign: 'start' }}
                  >
                    Coming Soon!
                  </div>
                </div>

                <div className={styles.Vs}>VS</div>

                <div className={styles.Team} style={{ justifyContent: 'flex-end' }}>
                  <div
                    className={styles.TeamText}
                    style={{ height: '2em', marginRight: '.5em', textAlign: 'end' }}
                  >
                    Coming Soon!
                  </div>
                </div>

                <div
                  className={styles.TeamLogo}
                  style={{ borderRight: `2px solid ${constants.colors.cplYellow}` }}
                >
                </div>

              </div>
            </div>
          ) : (
            selectedWeekMatchups.map((matchup) => {
              const awayTeam = teams.find(t => t[constants.teamId] == matchup[constants.awayTeamId]);
              const homeTeam = teams.find(t => t[constants.teamId] == matchup[constants.homeTeamId]);

              return (
                <div onClick={gotoLineup} className={styles.MatchupRow} id={matchup[constants.matchupId]} key={matchup[constants.id]}>
                  <div className={styles.MatchupTeams}>

                    <div className={styles.TeamLogo} style={{ borderLeft: `2px solid ${constants.colors.cplYellow}` }}>
                      {awayTeam ? <img src={`/logos/${awayTeam[constants.clubLogo]}.png`} className={styles.Icon} /> : <></>}
                    </div>

                    <div id={matchup[constants.awayTeamId]} className={styles.Team} style={{ justifyContent: 'flex-start' }}>
                      <div className={styles.TeamText} style={{ height: '2em', marginRight: '.5em', textAlign: 'start' }}>
                        {awayTeam ? matchup[constants.awayName] + " (" + awayTeam[constants.ranking] + ")" : "BYE"}
                      </div>
                    </div>

                    {isDateInPast(matchup[constants.scheduledTime]) ?
                      <div className={styles.ScoreContainer}>
                        <div className={styles.Score} style={{ paddingLeft: '2px' }}>{matchup[constants.awayWins]}</div>
                        <div className={styles.Score} style={{ paddingRight: '2px' }}>{matchup[constants.homeWins]}</div>
                      </div>
                      : <div className={styles.Vs}>VS</div>}

                    <div id={matchup[constants.homeTeamId]} className={styles.Team} style={{ justifyContent: 'flex-end' }}>
                      <div className={styles.TeamText} style={{ height: '2em', marginRight: '.5em', textAlign: 'end' }}>
                        {homeTeam ? matchup[constants.homeName] + " (" + homeTeam[constants.ranking] + ")" : "BYE"}
                      </div>
                    </div>

                    <div className={styles.TeamLogo} style={{ borderRight: `2px solid ${constants.colors.cplYellow}` }}>
                      {homeTeam ? <img src={`/logos/${homeTeam[constants.clubLogo]}.png`} className={styles.Icon} /> : <></>}
                    </div>
                  </div>

                  <div className={styles.MatchTime}>
                    {new Date(matchup[constants.scheduledTime]).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true,
                    }).replace(',', ' -')}
                  </div>

                  <div className={styles.MatchTime}>
                    View lineup
                  </div>
                </div>
              );
            })
          )}
        </div>
      </> : <LoadingSpinner />}

    </div>
  )
}

export default Lineups;